import { boolCodec, nullableUuid, typeCodec } from "@iterate/common/types/basicTypes";
import {
  goalDeltaSchema,
  goalEntitySchema,
  goalObjectSchema,
  habitDeltaSchema,
  habitEntitySchema,
  habitObjectSchema,
  instanceOverrideDeltaSchema,
  instanceOverrideEntitySchema,
  instanceOverrideObjectSchema,
  journalDeltaSchema,
  journalEntitySchema,
  journalObjectSchema,
  metricDeltaSchema,
  metricEntitySchema,
  metricObjectSchema,
  metricSeriesDeltaSchema,
  metricSeriesEntitySchema,
  metricSeriesObjectSchema,
  taskDeltaSchema,
  taskEntitySchema,
  taskObjectSchema,
} from "@iterate/common/types/domain";
import { z } from "zod/v4";

/** Shared Dexie codecs for fields that match `baseTaskObjectSchema` / task-shaped rows. */
const taskLikeDexieFields = {
  isFolder: boolCodec,
  disabled: boolCodec,
  completed: boolCodec,
  parentId: nullableUuid,
  goalId: nullableUuid,
} satisfies z.ZodRawShape;

/** Instance overrides use `.partial()` task fields; codecs must stay optional at the row boundary. */
const instanceTaskLikeDexieFields = {
  isFolder: boolCodec.optional(),
  disabled: boolCodec.optional(),
  completed: boolCodec.optional(),
  parentId: nullableUuid.optional(),
  goalId: nullableUuid.optional(),
} satisfies z.ZodRawShape;

export const metricSeriesRecordSchema = metricSeriesObjectSchema.extend({
  type: typeCodec("metric_series"),
});
export const metricRecordSchema = metricObjectSchema.extend({
  type: typeCodec("metric"),
});
export const goalRecordSchema = goalObjectSchema.extend({
  type: typeCodec("goal"),
});
export const journalRecordSchema = journalObjectSchema.extend({
  type: typeCodec("journal"),
});
export const habitRecordSchema = habitObjectSchema.extend({
  ...taskLikeDexieFields,
  type: typeCodec("habit"),
});
export const instanceOverrideRecordSchema = instanceOverrideObjectSchema.extend({
  ...instanceTaskLikeDexieFields,
  type: typeCodec("instance"),
});
export const taskRecordSchema = taskObjectSchema.extend({
  ...taskLikeDexieFields,
  isProject: boolCodec,
  type: typeCodec("task"),
});

export type MetricSeriesRecord = z.output<typeof metricSeriesRecordSchema>;
export type MetricRecord = z.output<typeof metricRecordSchema>;
export type GoalRecord = z.output<typeof goalRecordSchema>;
export type JournalRecord = z.output<typeof journalRecordSchema>;
export type HabitRecord = z.output<typeof habitRecordSchema>;
export type InstanceOverrideRecord = z.output<typeof instanceOverrideRecordSchema>;
export type TaskRecord = z.output<typeof taskRecordSchema>;

export const syncEntityByType = {
  metric_series: {
    entitySchema: metricSeriesEntitySchema,
    deltaSchema: metricSeriesDeltaSchema,
    recordSchema: metricSeriesRecordSchema,
  },
  goal: {
    entitySchema: goalEntitySchema,
    deltaSchema: goalDeltaSchema,
    recordSchema: goalRecordSchema,
  },
  habit: {
    entitySchema: habitEntitySchema,
    deltaSchema: habitDeltaSchema,
    recordSchema: habitRecordSchema,
  },
  instance: {
    entitySchema: instanceOverrideEntitySchema,
    deltaSchema: instanceOverrideDeltaSchema,
    recordSchema: instanceOverrideRecordSchema,
  },
  journal: {
    entitySchema: journalEntitySchema,
    deltaSchema: journalDeltaSchema,
    recordSchema: journalRecordSchema,
  },
  metric: {
    entitySchema: metricEntitySchema,
    deltaSchema: metricDeltaSchema,
    recordSchema: metricRecordSchema,
  },
  task: {
    entitySchema: taskEntitySchema,
    deltaSchema: taskDeltaSchema,
    recordSchema: taskRecordSchema,
  },
} as const;

export type SyncEntityKind = keyof typeof syncEntityByType;
